import { FC, useContext, useMemo } from "react";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import sortBy from "lodash/sortBy";
import { BiSearch } from "react-icons/bi";
import { setDoc } from "firebase/firestore";
import { useDebouncedState } from "@mantine/hooks";
import { useCollection } from "react-firebase-hooks/firestore";
import { Anchor, Box, Switch, Table, TextInput } from "@mantine/core";

import { Header } from "@components/Header/Header";
import { Content } from "@components/Content/Content";
import { PageLoader } from "@components/PageLoader/PageLoader";

import {
  getUserDocRef,
  getUserCollectionRef,
} from "../../services/user.services";
import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

import { IUser } from "@models/user.model";

export const UsersRoute: FC = () => {
  const [q, setQ] = useDebouncedState("", 500);

  const { t } = useTranslation();

  const {
    user: { isAdmin },
    authUser: { uid: myUid },
  } = useContext(AuthContext)!;

  const navigate = useNavigate();

  const [collection, isLoading] = useCollection(getUserCollectionRef());

  const users = useMemo(
    () =>
      sortBy(
        (collection?.docs || []).reduce((pv, doc) => {
          const user = {
            ...(doc.data() as unknown as IUser),
            uid: doc.id,
          };

          const { email, firstName, lastName } = user;
          if (
            email
              .trim()
              .toLocaleLowerCase()
              .includes(q.toLocaleLowerCase().trim()) ||
            firstName
              .trim()
              .toLocaleLowerCase()
              .includes(q.toLocaleLowerCase().trim()) ||
            lastName
              .trim()
              .toLocaleLowerCase()
              .includes(q.toLocaleLowerCase().trim())
          )
            return [...pv, user];
          return pv;
        }, [] as (IUser & { uid: string })[]),
        (user) => -(user.isAdmin ? 1 : 0)
      ),
    [collection?.docs, q]
  );

  const toggleIsAdmin = (uid: string, change: IUser["isAdmin"]) => {
    setDoc(getUserDocRef(uid), { isAdmin: change } as Partial<IUser>, {
      merge: true,
    });
  };

  if (!isAdmin) navigate("/portal");

  if (isLoading || !collection) return <PageLoader />;

  return (
    <>
      <Header />
      <Content>
        <Box>
          <TextInput
            w={300}
            icon={<BiSearch />}
            onChange={(e) => {
              setQ(e.target.value.toLocaleLowerCase());
            }}
            placeholder={`${t("Search")}...`}
          />
        </Box>
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th>{t("First Name")}</th>
              <th>{t("Last Name")}</th>
              <th>{t("Email")}</th>
              <th>{t("Admin Permissions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(({ uid, firstName, lastName, email, isAdmin }) => (
              <tr key={uid}>
                <td>{firstName}</td>
                <td>{lastName}</td>
                <td>
                  <Anchor href={`mailto:${email}`}>{email}</Anchor>
                </td>
                <td>
                  <Switch
                    checked={!!isAdmin}
                    disabled={myUid === uid}
                    onChange={() => {
                      toggleIsAdmin(uid, !isAdmin);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Content>
    </>
  );
};
